import _merge from 'lodash/merge'
import type { ConditionalPick, IsNever, KeyAsString, ValueOf } from 'type-fest'
import type { ZodTypeAny } from 'zod'

import type { Step } from '@flows/configs/types'
import type { NoInfer } from '@flows/configs/types/helperTypes'
import type { AnyCustomComponent } from '@flows/core/types/CustomComponent'
import type { AnyCallbackConfig, AnyDispatchHook } from '@flows/core/types/dispatchedHookTypes'
import type {
  StepContentElement,
  StepContentFormElementCommonProps,
  TrackingOptions,
} from '@flows/core/types/stepContentTypes'
import type {
  AnyStepConfiguration,
  AnySteps,
  ProgressBarSection,
  StepConfigurationOptions,
} from '@flows/core/types/stepTypes'
import type { ProductKey } from '@flows/index'
import type { ThunkAction } from '@reduxjs/toolkit'

export type Constants<
  TStepName extends Step,
  TSchema extends ZodTypeAny,
  TExtra extends Record<string, any>,
> = {
  FIRST_STEP_NAME: TStepName
} & Partial<{
  POST_QUOTE_STEP: TStepName
  SCHEMA: TSchema
}> &
  TExtra

export type FlowConfigurationOptions<
  TConstants extends Constants<any, any, any>,
  TInitialHelperValues,
  TInitialValues,
> = {
  constants: TConstants
} & Partial<{
  getInitialValues: () => TInitialValues
  getHelperInitialValues: () => TInitialHelperValues
  progressBarSections: ProgressBarSection[]
}>

type StepWithStepContent<TStep extends AnyStepConfiguration> = Exclude<
  TStep,
  Record<string, any> & { stepContent?: never }
>

type ExtractDispatchHookStepKey<
  TStep extends AnyStepConfiguration,
  TKey extends keyof TStep,
> = ValueOf<ConditionalPick<Extract<TStep, { [K in TKey]: AnyDispatchHook }>, AnyDispatchHook>>

type ExtractCustomComponentType<TStep extends AnyStepConfiguration> = Extract<
  NonNullable<StepWithStepContent<TStep>['stepContent']>[number],
  AnyCustomComponent
>

type ExtractCallbackConfigType<
  TStep extends AnyStepConfiguration,
  TStepContent = NonNullable<StepWithStepContent<TStep>['stepContent']>[number],
  // Get the form elements since they might contain dispatched hooks
  TStepFormElement = Extract<TStepContent, StepContentFormElementCommonProps<any, any>>,
  // Get hooks from form elements
  TStepContentHooks = ValueOf<ConditionalPick<TStepFormElement, AnyDispatchHook>>,
  // Get steps with on submit and with onPrepare hooks
  TStepHooks =
    | ExtractDispatchHookStepKey<TStep, 'onPrepare'>
    | ExtractDispatchHookStepKey<TStep, 'onSubmit'>,
> =
  | ValueOf<ConditionalPick<TStepHooks, AnyCallbackConfig>>
  | ValueOf<ConditionalPick<TStepContentHooks, AnyCallbackConfig>>

export type FlowConfiguration<
  TSteps extends AnySteps,
  // Get the values types
  TInitialHelperValues,
  TInitialValues,
  TThunkAction extends ThunkAction<any, any, any, any>,
  // Get the Step and the StepContent types to be used next
  TStep extends AnyStepConfiguration = ValueOf<TSteps>,
  TStepName extends Step = KeyAsString<TSteps>,
  TConstants extends Constants<any, any, any> = Constants<NoInfer<TStepName>, ZodTypeAny, any>,
  // Get the custom component type
  TCustomComponent extends AnyCustomComponent = ExtractCustomComponentType<TStep>,
  TCallbackConfig = ExtractCallbackConfigType<TStep>,
  TFlowConfigurationOptions = FlowConfigurationOptions<
    TConstants,
    TInitialValues,
    TInitialHelperValues
  >,
  TServices extends string[] = Extract<TStep, { beforeLoad: string[] }>['beforeLoad'],
  TService extends string = TServices extends (infer R)[] ? R : never,
  TStepConfigurationOptions = StepConfigurationOptions<
    TCallbackConfig extends AnyCallbackConfig ? TCallbackConfig : never,
    TCustomComponent,
    TThunkAction,
    TService
  >,
> = {
  steps: TSteps
} & TFlowConfigurationOptions &
  TStepConfigurationOptions

export const getValuesToTrack = <TSteps extends AnySteps>(steps: TSteps) => {
  return Object.values(steps)
    .flatMap((step) => {
      if (step.stepContent === undefined) {
        return
      }
      return step.stepContent.flatMap((input: StepContentElement<any, any, any>) => {
        if (input.props !== undefined) {
          const prop = input.props as TrackingOptions
          if (prop.shouldTrackValues) {
            return input.outputs
          }
        } else if (input.shouldTrackValues) {
          if (input.type === 'customComponent') {
            return input.outputs
          }
          return input.name
        }
        return []
      })
    })
    .filter(Boolean)
}

export const getFlowConfiguration = <
  TSteps extends AnySteps,
  // Get the values types
  TInitialHelperValues,
  TInitialValues,
  // Get the Step and the StepContent types to be used next
  TStep extends AnyStepConfiguration = ValueOf<TSteps>,
  TStepName extends Step = KeyAsString<TSteps>,
  TConstants extends Constants<any, any, Record<string, any>> = Constants<
    NoInfer<TStepName>,
    ZodTypeAny,
    Record<string, any>
  >,
  TServicesBase = Extract<TStep, { beforeLoad: string[] }>['beforeLoad'],
  TServices = IsNever<TServicesBase> extends true ? never[] : TServicesBase,
  TService = TServices extends (infer R)[] ? R : unknown,
  // Get the custom component type
  TCustomComponent extends AnyCustomComponent = ExtractCustomComponentType<TStep>,
  TCallbackConfig = ExtractCallbackConfigType<TStep>,
>(
  steps: TSteps,
  options: FlowConfigurationOptions<TConstants, TInitialValues, TInitialHelperValues>
) => {
  const valuesToTrack = getValuesToTrack(steps)

  const optionsWithTrackingValues: FlowConfigurationOptions<
    TConstants & { valuesToTrack: string[] },
    TInitialValues,
    TInitialHelperValues
  > = {
    ...options,
    constants: { ...options.constants, valuesToTrack },
  }

  return {
    getSteps: () => steps,
    getOptions: () => optionsWithTrackingValues,
    init: <TThunkAction extends ThunkAction<any, any, any, any>>(
      params: StepConfigurationOptions<
        TCallbackConfig extends AnyCallbackConfig ? TCallbackConfig : never,
        TCustomComponent,
        TThunkAction,
        TService extends string ? TService : string
      >
    ) =>
      ({
        steps,
        ...optionsWithTrackingValues,
        ...params,
      }) as const,
  }
}

export const getProductFlowConfiguration = <
  TProductKey extends ProductKey,
  TSteps extends AnySteps,
  // Get the Step and the StepContent types to be used next
  TStepName extends KeyAsString<TSteps>,
  TConstants extends Constants<NoInfer<TStepName>, ZodTypeAny, Record<string, any>>,
>(
  _productKey: TProductKey,
  steps: TSteps,
  options: FlowConfigurationOptions<
    TConstants,
    Partial<Record<TProductKey, Record<string, any>>>,
    Record<TProductKey, { productKey: TProductKey; configuration_parameters: Record<string, any> }>
  >
) => {
  return getFlowConfiguration(steps, options)
}
